// Noticias.js - Componente de Notícias
// ✅ Busca automática de notícias de feeds RSS/Atom
// ✅ Configurações por utilizador (via API / Dashboard)
// ✅ Pesquisa de notícias
// ✅ Atualização automática a cada 10 minutos
// ✅ Suporte a Dark Mode
// ✅ Skeleton loading para melhor UX
// ✅ Popup/modal para ler notícia completa
// ✅ Modal renderizado via React Portal → cobre SEMPRE todo o ecrã
// ✅ Modal abre IMEDIATAMENTE com o que já temos + conteúdo completo carrega em background
// ✅ Cache em memória por utilizador (segunda abertura = instantânea)
// ✅ Fallback de imagens em vários níveis (incluindo lazy loading)
// ✅ Bloqueio de scroll do body quando o modal está aberto
// 🔥 CORRIGIDO: Todos os pedidos passam pelo backend Express (localhost:3000)
// 🔥 CORRIGIDO: Extração de imagens melhorada (og:image, data-src, srcset)
// 🔥 CORRIGIDO: O componente agora observa o `userId` para limpar caches ao trocar de utilizador
// 🔥 CORRIGIDO: As notícias são buscadas POR UTILIZADOR LOGADO (config do user via API)
// 🔥 CORRIGIDO: Recebe `noticiasConfig` do Dashboard (fonte da verdade) e sincroniza
// 🔥 CORRIGIDO: Busca a configuração do utilizador via API (/api/users/me) como fallback
// 🔥 NOVO:    Fallback PROATIVO de imagens (não depende só do onError)
// 🔥 NOVO:    Estado 'loading'/'failed' por notícia para evitar pedidos duplicados
// 🔥 NOVO:    Descarta imagens inválidas (data:, 1x1, pixel de tracking)
// 🔥 NOVO:    Render dos cards mostra spinner durante o fallback

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { Icon } from './SharedComponents';
import './Noticias.css';

// ==============================================
// CACHE EM MEMÓRIA (fora do componente, persiste durante a sessão)
// 🔥 Cache por utilizador para evitar mistura entre contas
// ==============================================
const conteudoCache = new Map();     // { `userId::link`: { conteudo, imagem } }
const imagemCache = new Map();       // { url: url da imagem }

// URL do backend para o proxy
const BACKEND_URL = '/api/noticias/proxy?url=';

// ==============================================
// COMPONENTE PRINCIPAL
// ==============================================
export default function Noticias({ userId, noticiasConfig: noticiasConfigProp }) {
    const [noticias, setNoticias] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [pesquisa, setPesquisa] = useState('');
    const [fontes, setFontes] = useState([]);
    const [noticiasFiltradas, setNoticiasFiltradas] = useState([]);
    const [erro, setErro] = useState(null);
    const [atualizando, setAtualizando] = useState(false);
    const [moduloAtivo, setModuloAtivo] = useState(true);
    const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);
    const [fontesComErro, setFontesComErro] = useState([]);

    // ==============================================
    // 🔥 ESTADO PARA O MODAL DA NOTÍCIA
    // ==============================================
    const [noticiaModal, setNoticiaModal] = useState(null);
    const [carregandoConteudo, setCarregandoConteudo] = useState(false);
    const [conteudoNoticia, setConteudoNoticia] = useState('');
    const [imagemModal, setImagemModal] = useState(null);
    const [imagensFallback, setImagensFallback] = useState({});

    // Ref para evitar setState depois do unmount
    const isMountedRef = useRef(true);
    useEffect(() => {
        isMountedRef.current = true;
        return () => { isMountedRef.current = false; };
    }, []);

    // Ref para guardar o userId "ativo" (evita race conditions em pedidos async)
    const userIdRef = useRef(userId);
    useEffect(() => {
        userIdRef.current = userId;
    }, [userId]);

    // ==============================================
    // 🔥 CHAVE DE CACHE POR UTILIZADOR
    // ==============================================
    const cacheKey = (link) => `${userId || 'anon'}::${link}`;

    // ==============================================
    // 🔥 BUSCAR CONFIGURAÇÃO DE NOTÍCIAS DO UTILIZADOR LOGADO (API)
    // ==============================================
    const buscarConfigDoUtilizador = async () => {
        // 1) Se o Dashboard já nos passou a config, usamos essa (fonte da verdade)
        if (noticiasConfigProp) {
            return {
                ativo: noticiasConfigProp.ativo !== undefined ? noticiasConfigProp.ativo : true,
                fontes: noticiasConfigProp.fontes || []
            };
        }

        // 2) Caso contrário, tentamos buscar à API (por utilizador logado)
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                return null;
            }

            const response = await axios.get('/api/users/me', {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 10000
            });

            if (response.data && response.data.success) {
                let userData = response.data.data || response.data;
                if (userData.usuario) userData = userData.usuario;

                if (userData.noticiasConfig) {
                    return {
                        ativo: userData.noticiasConfig.ativo !== undefined ? userData.noticiasConfig.ativo : true,
                        fontes: userData.noticiasConfig.fontes || []
                    };
                }
            }
        } catch (e) {
            console.warn('⚠️ Não foi possível buscar config de notícias do utilizador via API:', e?.message);
        }

        // 3) Fallback: localStorage (compatibilidade com versões antigas)
        try {
            const configLocal = JSON.parse(localStorage.getItem('noticias_config') || 'null');
            if (configLocal) {
                return {
                    ativo: configLocal.ativo !== undefined ? configLocal.ativo : true,
                    fontes: configLocal.fontes || []
                };
            }
        } catch (e) { /* ignora */ }

        return null;
    };

    // ==============================================
    // CARREGAR CONFIGURAÇÕES E NOTÍCIAS (OBSERVA O userId)
    // 🔥 Busca config POR UTILIZADOR e só depois busca notícias
    // ==============================================
    useEffect(() => {
        // 🔥 LIMPAR CACHES E ESTADOS QUANDO O userId MUDA
        // (limpa apenas entradas deste utilizador para não afetar outros)
        const prefixo = `${userId || 'anon'}::`;
        Array.from(conteudoCache.keys()).forEach((k) => {
            if (k.startsWith(prefixo)) conteudoCache.delete(k);
        });
        setImagensFallback({});
        setNoticias([]);
        setNoticiasFiltradas([]);
        setPesquisa('');
        setErro(null);
        setUltimaAtualizacao(null);
        setFontesComErro([]);
        setCarregando(true);

        // 🔥 NOVO FLUXO: primeiro carrega a config do utilizador, depois as notícias
        const inicializar = async () => {
            const config = await buscarConfigDoUtilizador();

            if (!isMountedRef.current) return;

            if (!config) {
                // Sem config → módulo desativado por defeito
                setModuloAtivo(false);
                setFontes([]);
                setCarregando(false);
                setErro('Nenhuma configuração de notícias encontrada para este utilizador.');
                return;
            }

            setModuloAtivo(config.ativo !== false);
            setFontes(config.fontes || []);

            // Sincronizar localStorage (para compatibilidade com componentes antigos)
            try {
                localStorage.setItem('noticias_config', JSON.stringify({
                    ativo: config.ativo !== false,
                    fontes: config.fontes || []
                }));
            } catch (e) { /* ignora */ }

            // Se o módulo estiver desativado, não busca notícias
            if (config.ativo === false) {
                setCarregando(false);
                return;
            }

            // Buscar notícias com as fontes do utilizador
            await carregarNoticiasComFontes(config.fontes || [], false);
        };

        inicializar();

        // Atualizar a cada 10 minutos
        const intervalo = setInterval(() => {
            carregarNoticias(true);
        }, 10 * 60 * 1000);

        return () => clearInterval(intervalo);
        
    }, [userId, noticiasConfigProp]);

    // ==============================================
    // FILTRAR NOTÍCIAS QUANDO A PESQUISA MUDAR
    // ==============================================
    useEffect(() => {
        if (pesquisa.trim() === '') {
            setNoticiasFiltradas(noticias);
        } else {
            const filtradas = noticias.filter(n =>
                n.titulo?.toLowerCase().includes(pesquisa.toLowerCase()) ||
                n.resumo?.toLowerCase().includes(pesquisa.toLowerCase()) ||
                n.fonte?.toLowerCase().includes(pesquisa.toLowerCase())
            );
            setNoticiasFiltradas(filtradas);
        }
    }, [pesquisa, noticias]);

    // ==============================================
    // 🔥 NOVO: FALLBACK PROATIVO DE IMAGENS
    // Para cada notícia SEM imagem, tenta buscar og:image no artigo
    // - Limite de 5 pedidos em paralelo (evita sobrecarregar o backend)
    // - Marca 'loading' para não repetir
    // - Marca 'failed' se não encontrar (evita loop infinito)
    // ==============================================
    useEffect(() => {
        if (carregando || noticias.length === 0) return;

        // Só notícias sem imagem e que ainda não foram tentadas
        const noticiasSemImagem = noticias.filter(
            (n) =>
                !n.imagem &&
                !imagensFallback[n.id] &&
                n.link &&
                n.link !== '#'
        );

        if (noticiasSemImagem.length === 0) return;

        // Limita a 5 pedidos por lote
        const lote = noticiasSemImagem.slice(0, 5);

        // Marca como 'loading' de imediato (evita duplicação entre renders)
        setImagensFallback((prev) => {
            const novo = { ...prev };
            lote.forEach((n) => {
                if (!novo[n.id]) novo[n.id] = 'loading';
            });
            return novo;
        });

        // Dispara buscas
        lote.forEach(async (noticia) => {
            const fallback = await buscarImagemFallback(noticia.link);

            if (!isMountedRef.current) return;

            setImagensFallback((prev) => ({
                ...prev,
                [noticia.id]: fallback || 'failed'
            }));
        });
        
    }, [noticias, carregando, imagensFallback]);

    // ==============================================
    // 🔥 ESC fecha o modal + bloqueia scroll do body
    // ==============================================
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape' && noticiaModal) {
                fecharModalNoticia();
            }
        };

        document.addEventListener('keydown', handleEsc);

        // Bloquear scroll do body quando o modal está aberto
        if (noticiaModal) {
            const scrollOriginal = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.removeEventListener('keydown', handleEsc);
                document.body.style.overflow = scrollOriginal;
            };
        }

        return () => document.removeEventListener('keydown', handleEsc);
        
    }, [noticiaModal]);

    // ==============================================
    // CARREGAR NOTÍCIAS DE TODAS AS FONTES
    // 🔥 Aceita `fontesParam` para usar as fontes do utilizador
    // ==============================================
    const carregarNoticiasComFontes = async (fontesParam, silencioso = false) => {
        const fontesParaUsar = (fontesParam || fontes || []).filter(f => f.ativo);
        const userIdAtual = userIdRef.current;

        if (!moduloAtivo && fontesParaUsar.length === 0) {
            setNoticias([]);
            setNoticiasFiltradas([]);
            setCarregando(false);
            return;
        }

        if (!silencioso) {
            setCarregando(true);
        } else {
            setAtualizando(true);
        }
        setErro(null);
        setFontesComErro([]);

        try {
            if (fontesParaUsar.length === 0) {
                setNoticias([]);
                setNoticiasFiltradas([]);
                setCarregando(false);
                setAtualizando(false);
                setErro('Nenhuma fonte de notícias ativa. Configure nas Definições.');
                return;
            }

            const promises = fontesParaUsar.map(fonte => buscarNoticiasDaFonte(fonte));
            const resultados = await Promise.allSettled(promises);

            // 🔥 Se o utilizador mudou entretanto, ignorar resultado
            if (userIdRef.current !== userIdAtual) {
                return;
            }

            const todasNoticias = [];
            const fontesFalharam = [];

            resultados.forEach((resultado, index) => {
                if (resultado.status === 'fulfilled' && resultado.value.length > 0) {
                    todasNoticias.push(...resultado.value);
                } else {
                    const fonteNome = fontesParaUsar[index]?.nome || 'Desconhecida';
                    fontesFalharam.push(fonteNome);
                    console.warn(`Falha ao carregar ${fonteNome}`);
                }
            });

            const uniqueNoticias = [];
            const seen = new Set();
            todasNoticias.forEach(noticia => {
                const key = `${noticia.titulo?.toLowerCase().trim()}-${noticia.fonte}`;
                if (!seen.has(key) && noticia.titulo?.length > 5) {
                    seen.add(key);
                    uniqueNoticias.push(noticia);
                }
            });

            uniqueNoticias.sort((a, b) => new Date(b.data) - new Date(a.data));

            if (!isMountedRef.current) return;

            setNoticias(uniqueNoticias);
            setNoticiasFiltradas(uniqueNoticias);
            setUltimaAtualizacao(new Date());
            setFontesComErro(fontesFalharam);

            if (uniqueNoticias.length === 0) {
                setErro('Não foi possível carregar notícias. Verifique as fontes configuradas.');
            } else if (fontesFalharam.length > 0) {
                setErro(`Algumas fontes falharam: ${fontesFalharam.join(', ')}. ${uniqueNoticias.length} notícias carregadas.`);
            }
        } catch (e) {
            console.error('Erro ao carregar notícias:', e);
            if (isMountedRef.current) {
                setErro('Erro ao carregar notícias. Tente novamente mais tarde.');
            }
        } finally {
            if (isMountedRef.current) {
                setCarregando(false);
                setAtualizando(false);
            }
        }
    };

    // ==============================================
    // CARREGAR NOTÍCIAS (wrapper para o botão Atualizar)
    // 🔥 Re-busca config do utilizador antes de atualizar
    // ==============================================
    const carregarNoticias = async (silencioso = false) => {
        const config = await buscarConfigDoUtilizador();

        if (!isMountedRef.current) return;

        if (!config || config.ativo === false) {
            setModuloAtivo(config?.ativo !== false);
            setNoticias([]);
            setNoticiasFiltradas([]);
            setCarregando(false);
            setAtualizando(false);
            if (!config) {
                setErro('Nenhuma configuração de notícias encontrada para este utilizador.');
            }
            return;
        }

        setModuloAtivo(true);
        setFontes(config.fontes || []);

        try {
            localStorage.setItem('noticias_config', JSON.stringify({
                ativo: true,
                fontes: config.fontes || []
            }));
        } catch (e) { /* ignora */ }

        await carregarNoticiasComFontes(config.fontes || [], silencioso);
    };

    // ==============================================
    // BUSCAR NOTÍCIAS DE UMA FONTE ESPECÍFICA
    // ==============================================
    const buscarNoticiasDaFonte = async (fonte) => {
        const metodos = [
            () => buscarViaBackend(fonte, fonte.url),
            () => buscarViaGoogleNews(fonte),
            () => buscarViaRSS2JSON(fonte),
            () => buscarViaAllOrigins(fonte),
            () => buscarViaCORSProxy(fonte),
            () => extrairNoticiasAlternativo(fonte)
        ];

        for (const metodo of metodos) {
            try {
                const resultado = await metodo();
                if (resultado && resultado.length > 0) {
                    console.log(`✅ ${fonte.nome}: ${resultado.length} notícias carregadas`);
                    return resultado;
                }
            } catch (e) {
                // Silencioso para não poluir a consola
            }
        }

        return [];
    };

    // ==============================================
    // MÉTODO PRINCIPAL: BACKEND PROXY
    // ==============================================
    const buscarViaBackend = async (fonte, urlAlvo) => {
        const response = await axios.get(`${BACKEND_URL}${encodeURIComponent(urlAlvo)}`, {
            timeout: 15000
        });

        if (!response.data) return [];

        try {
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(response.data, 'text/xml');
            const parseError = xmlDoc.querySelector('parsererror');
            if (!parseError) {
                const items = xmlDoc.querySelectorAll('item');
                if (items.length > 0) return parseRSSFeed(items, fonte);

                const entries = xmlDoc.querySelectorAll('entry');
                if (entries.length > 0) return parseAtomFeed(entries, fonte);
            }
        } catch (e) { /* continua para HTML */ }

        return extrairNoticiasDoHTML(response.data, fonte);
    };

    // ==============================================
    // MÉTODO 1: RSS2JSON (fallback)
    // ==============================================
    const buscarViaRSS2JSON = async (fonte) => {
        try {
            const response = await axios.get(
                `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(fonte.url)}`,
                { timeout: 15000 }
            );

            if (response.data?.status === 'ok' && response.data?.items?.length > 0) {
                return response.data.items.map(item => ({
                    id: `${fonte.id}-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
                    titulo: item.title?.trim() || 'Sem título',
                    link: item.link || '#',
                    resumo: item.description?.replace(/<[^>]*>/g, '').trim() || item.title || 'Sem descrição',
                    data: new Date(item.pubDate || Date.now()),
                    fonte: fonte.nome,
                    fonteUrl: fonte.url,
                    imagem: item.thumbnail || item.enclosure?.link || null,
                    fonteId: fonte.id
                })).filter(n => n.titulo && n.titulo.length > 5);
            }
        } catch (e) { }
        return [];
    };

    // ==============================================
    // MÉTODO 2: ALL ORIGINS PROXY (fallback)
    // ==============================================
    const buscarViaAllOrigins = async (fonte) => {
        try {
            const proxyUrl = 'https://api.allorigins.win/raw?url=';
            const response = await axios.get(`${proxyUrl}${encodeURIComponent(fonte.url)}`, {
                timeout: 15000
            });

            if (!response.data) return [];

            try {
                const parser = new DOMParser();
                const xmlDoc = parser.parseFromString(response.data, 'text/xml');
                const parseError = xmlDoc.querySelector('parsererror');
                if (!parseError) {
                    const items = xmlDoc.querySelectorAll('item');
                    if (items.length > 0) return parseRSSFeed(items, fonte);

                    const entries = xmlDoc.querySelectorAll('entry');
                    if (entries.length > 0) return parseAtomFeed(entries, fonte);
                }
            } catch (e) { }

            return extrairNoticiasDoHTML(response.data, fonte);
        } catch (e) { }
        return [];
    };

    // ==============================================
    // MÉTODO 3: CORS PROXY (fallback)
    // ==============================================
    const buscarViaCORSProxy = async (fonte) => {
        const proxies = [
            'https://corsproxy.io/?',
            'https://cors-anywhere.herokuapp.com/'
        ];

        for (const proxy of proxies) {
            try {
                const response = await axios.get(`${proxy}${encodeURIComponent(fonte.url)}`, {
                    timeout: 15000
                });

                if (response.data) {
                    try {
                        const parser = new DOMParser();
                        const xmlDoc = parser.parseFromString(response.data, 'text/xml');
                        const parseError = xmlDoc.querySelector('parsererror');
                        if (!parseError) {
                            const items = xmlDoc.querySelectorAll('item');
                            if (items.length > 0) return parseRSSFeed(items, fonte);

                            const entries = xmlDoc.querySelectorAll('entry');
                            if (entries.length > 0) return parseAtomFeed(entries, fonte);
                        }
                    } catch (e) { }

                    return extrairNoticiasDoHTML(response.data, fonte);
                }
            } catch (e) { }
        }
        return [];
    };

    // ==============================================
    // MÉTODO 4: GOOGLE NEWS (via backend)
    // ==============================================
    const buscarViaGoogleNews = async (fonte) => {
        try {
            const keywords = fonte.nome
                .replace(/[0-9]/g, '')
                .replace(/[^a-zA-Z\s]/g, '')
                .trim();

            const query = encodeURIComponent(`${keywords} Moçambique notícias economia`);
            const googleUrl = `https://news.google.com/rss/search?q=${query}&hl=pt&gl=MZ&ceid=MZ:pt`;

            const response = await axios.get(`${BACKEND_URL}${encodeURIComponent(googleUrl)}`, {
                timeout: 15000
            });

            if (!response.data) return [];

            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(response.data, 'text/xml');
            const parseError = xmlDoc.querySelector('parsererror');
            if (parseError) return [];

            const items = xmlDoc.querySelectorAll('item');
            if (items.length === 0) return [];

            return parseRSSFeed(items, { ...fonte, nome: `Google News • ${fonte.nome}` })
                .map(n => ({ ...n, titulo: n.titulo.replace(/\([^)]*\)/g, '').trim() }))
                .slice(0, 10);
        } catch (e) {
            return [];
        }
    };

    // ==============================================
    // EXTRAIR NOTÍCIAS DO HTML (fallback)
    // ==============================================
    const extrairNoticiasDoHTML = (html, fonte) => {
        try {
            const noticias = [];
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');

            const seletores = [
                'article', '.post', '.news-item', '.story', '.card',
                '.list-item', 'li.news', 'div[class*="news"]',
                'div[class*="post"]', 'div[class*="article"]'
            ];

            let elementos = [];
            for (const seletor of seletores) {
                const encontrados = doc.querySelectorAll(seletor);
                if (encontrados.length > 0) {
                    elementos = encontrados;
                    break;
                }
            }

            if (elementos.length === 0) {
                const links = doc.querySelectorAll('a[href]');
                links.forEach(link => {
                    const titulo = link.textContent?.trim();
                    if (titulo && titulo.length > 15 && titulo.length < 200) {
                        const href = link.getAttribute('href');
                        if (href && !href.startsWith('#') && !href.startsWith('javascript:')) {
                            const img = link.querySelector('img');
                            let imagem = img ? img.getAttribute('src') : null;
                            if (imagem && !imagem.startsWith('http')) {
                                try { imagem = new URL(imagem, fonte.url).href; } catch (e) { imagem = null; }
                            }
                            const parent = link.closest('p, div, li');
                            const resumo = parent?.textContent?.trim()?.substring(0, 200) || titulo;

                            noticias.push({
                                id: `${fonte.id}-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
                                titulo: titulo,
                                link: href.startsWith('http') ? href : new URL(href, fonte.url).href,
                                resumo: resumo,
                                data: new Date(),
                                fonte: fonte.nome,
                                fonteUrl: fonte.url,
                                imagem: imagem || null,
                                fonteId: fonte.id
                            });
                        }
                    }
                });
                return noticias.slice(0, 20);
            }

            elementos.forEach(el => {
                try {
                    const tituloEl = el.querySelector('h1, h2, h3, h4, .title, .headline, .heading');
                    const titulo = tituloEl?.textContent?.trim() ||
                        el.getAttribute('title') ||
                        el.textContent?.trim().substring(0, 100);

                    if (!titulo || titulo.length < 5) return;

                    const linkEl = el.querySelector('a[href]') || el;
                    let link = linkEl.getAttribute('href') || '#';
                    if (link && !link.startsWith('http')) {
                        try { link = new URL(link, fonte.url).href; } catch (e) { }
                    }

                    const resumoEl = el.querySelector('p, .description, .excerpt, .summary, .content');
                    const resumo = resumoEl?.textContent?.trim() ||
                        el.textContent?.trim().replace(titulo, '').substring(0, 200) ||
                        titulo;

                    // Extração de imagem melhorada (lazy loading)
                    const imgEl = el.querySelector('img');
                    let imagem = null;
                    if (imgEl) {
                        imagem = imgEl.getAttribute('src') || imgEl.getAttribute('data-src') || imgEl.getAttribute('data-lazy-src');
                        if (!imagem && imgEl.hasAttribute('srcset')) {
                            imagem = imgEl.getAttribute('srcset').split(',')[0].trim().split(' ')[0];
                        }
                        if (imagem && !imagem.startsWith('http')) {
                            try { imagem = new URL(imagem, fonte.url).href; } catch (e) { imagem = null; }
                        }
                    }

                    const dataEl = el.querySelector('time, .date, .published, .timestamp');
                    let data = new Date();
                    if (dataEl) {
                        const dataStr = dataEl.getAttribute('datetime') || dataEl.textContent;
                        const parsed = new Date(dataStr);
                        if (!isNaN(parsed)) data = parsed;
                    }

                    if (titulo && titulo.length > 5) {
                        noticias.push({
                            id: `${fonte.id}-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
                            titulo: titulo,
                            link: link,
                            resumo: resumo,
                            data: data,
                            fonte: fonte.nome,
                            fonteUrl: fonte.url,
                            imagem: imagem,
                            fonteId: fonte.id
                        });
                    }
                } catch (e) { }
            });

            return noticias.slice(0, 30);
        } catch (e) {
            return [];
        }
    };

    // ==============================================
    // PARSE DE FEED RSS (MELHORADO COM MEDIA:TAGS)
    // ==============================================
    const parseRSSFeed = (items, fonte) => {
        const noticias = [];
        items.forEach(item => {
            try {
                const titulo = item.querySelector('title')?.textContent?.trim() || '';
                const link = item.querySelector('link')?.textContent?.trim() ||
                    item.querySelector('link')?.getAttribute('href') ||
                    item.querySelector('guid')?.textContent?.trim() || '#';
                const descricao = item.querySelector('description')?.textContent?.trim() ||
                    item.querySelector('summary')?.textContent?.trim() || '';
                const dataStr = item.querySelector('pubDate')?.textContent?.trim() ||
                    item.querySelector('published')?.textContent?.trim() ||
                    item.querySelector('updated')?.textContent?.trim() || '';
                const data = dataStr ? new Date(dataStr) : new Date();

                let imagem = null;

                // 1. Media tags (comuns em feeds da DW, VOA, etc.)
                const mediaContent = item.querySelector('content') || item.querySelector('thumbnail');
                if (mediaContent && mediaContent.getAttribute('url')) {
                    imagem = mediaContent.getAttribute('url');
                }

                // 2. Enclosure
                if (!imagem) {
                    const enclosure = item.querySelector('enclosure');
                    if (enclosure && enclosure.getAttribute('type')?.startsWith('image/')) {
                        imagem = enclosure.getAttribute('url');
                    }
                }

                // 3. Content:encoded
                if (!imagem) {
                    const conteudo = item.querySelector('content\\:encoded') || item.querySelector('encoded');
                    if (conteudo) {
                        const imgMatch = conteudo.textContent.match(/<img[^>]+(?:src|data-src|data-lazy-src)=["']([^"']+)["']/i);
                        if (imgMatch) imagem = imgMatch[1];
                    }
                }

                // 4. Descrição
                if (!imagem && descricao) {
                    const imgMatch = descricao.match(/<img[^>]+(?:src|data-src|data-lazy-src)=["']([^"']+)["']/i);
                    if (imgMatch) imagem = imgMatch[1];
                }

                // Resolver URLs relativas
                if (imagem && !imagem.startsWith('http')) {
                    try { imagem = new URL(imagem, fonte.url).href; } catch (e) { imagem = null; }
                }

                const descricaoLimpa = descricao.replace(/<[^>]*>/g, '').trim();

                if (titulo) {
                    noticias.push({
                        id: `${fonte.id}-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
                        titulo: titulo,
                        link: link,
                        resumo: descricaoLimpa || titulo || 'Sem descrição',
                        data: data,
                        fonte: fonte.nome,
                        fonteUrl: fonte.url,
                        imagem: imagem || null,
                        fonteId: fonte.id
                    });
                }
            } catch (e) { }
        });
        return noticias;
    };

    // ==============================================
    // PARSE DE FEED ATOM (MELHORADO)
    // ==============================================
    const parseAtomFeed = (entries, fonte) => {
        const noticias = [];
        entries.forEach(entry => {
            try {
                const titulo = entry.querySelector('title')?.textContent?.trim() || '';
                const link = entry.querySelector('link')?.getAttribute('href') ||
                    entry.querySelector('link')?.textContent?.trim() || '#';
                const descricao = entry.querySelector('summary')?.textContent?.trim() ||
                    entry.querySelector('content')?.textContent?.trim() || '';
                const dataStr = entry.querySelector('updated')?.textContent?.trim() ||
                    entry.querySelector('published')?.textContent?.trim() || '';
                const data = dataStr ? new Date(dataStr) : new Date();

                let imagem = null;
                const imgMatch = descricao.match(/<img[^>]+(?:src|data-src|data-lazy-src)=["']([^"']+)["']/i);
                if (imgMatch) imagem = imgMatch[1];

                if (imagem && !imagem.startsWith('http')) {
                    try { imagem = new URL(imagem, fonte.url).href; } catch (e) { imagem = null; }
                }

                const descricaoLimpa = descricao.replace(/<[^>]*>/g, '').trim();

                if (titulo) {
                    noticias.push({
                        id: `${fonte.id}-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
                        titulo: titulo,
                        link: link,
                        resumo: descricaoLimpa || titulo || 'Sem descrição',
                        data: data,
                        fonte: fonte.nome,
                        fonteUrl: fonte.url,
                        imagem: imagem || null,
                        fonteId: fonte.id
                    });
                }
            } catch (e) { }
        });
        return noticias;
    };

    // ==============================================
    // MÉTODO ALTERNATIVO (FALLBACK FINAL)
    // ==============================================
    const extrairNoticiasAlternativo = async (fonte) => {
        try {
            const query = encodeURIComponent(`Moçambique economia notícias ${fonte.nome}`);
            const url = `https://news.google.com/rss/search?q=${query}&hl=pt&gl=MZ&ceid=MZ:pt`;

            const response = await axios.get(`${BACKEND_URL}${encodeURIComponent(url)}`, {
                timeout: 15000
            });

            if (!response.data) return [];

            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(response.data, 'text/xml');
            if (xmlDoc.querySelector('parsererror')) return [];

            const items = xmlDoc.querySelectorAll('item');
            if (items.length === 0) return [];

            return parseRSSFeed(items, { ...fonte, nome: `Google News • ${fonte.nome}` })
                .map(n => ({ ...n, titulo: n.titulo.replace(/\([^)]*\)/g, '').trim() }))
                .slice(0, 10);
        } catch (e) {
            return [];
        }
    };

    // ==============================================
    // 🔥 BUSCAR IMAGEM DE FALLBACK VIA BACKEND (MELHORADO)
    // ==============================================
    const buscarImagemFallback = async (url) => {
        if (!url || url === '#') return null;
        if (imagemCache.has(url)) return imagemCache.get(url);

        try {
            const response = await axios.get(`${BACKEND_URL}${encodeURIComponent(url)}`, {
                timeout: 8000
            });

            if (!response.data) return null;

            const parser = new DOMParser();
            const doc = parser.parseFromString(response.data, 'text/html');

            let img = doc.querySelector('meta[property="og:image"]')?.getAttribute('content')
                || doc.querySelector('meta[name="twitter:image"]')?.getAttribute('content')
                || doc.querySelector('meta[property="og:image:secure_url"]')?.getAttribute('content')
                || doc.querySelector('link[rel="image_src"]')?.getAttribute('href');

            if (!img) {
                const imgEl = doc.querySelector('article img, .article img, .post-content img, .entry-content img, main img');
                if (imgEl) {
                    img = imgEl.getAttribute('src') || imgEl.getAttribute('data-src') || imgEl.getAttribute('data-lazy-src');
                    if (!img && imgEl.hasAttribute('srcset')) {
                        img = imgEl.getAttribute('srcset').split(',')[0].trim().split(' ')[0];
                    }
                }
            }

            if (img && !img.startsWith('http')) {
                try { img = new URL(img, url).href; } catch (e) { img = null; }
            }

            // 🔥 NOVO: descartar imagens claramente inválidas (pixel de tracking, data URIs)
            if (img && (img.startsWith('data:') || img.includes('1x1') || img.includes('pixel'))) {
                img = null;
            }

            if (img) imagemCache.set(url, img);
            return img;
        } catch (e) {
            return null;
        }
    };

    // ==============================================
    // 🔥 ABRIR NOTÍCIA — ABERTURA RÁPIDA + CARREGAMENTO EM BACKGROUND
    // ==============================================
    const abrirNoticia = (noticia) => {
        setNoticiaModal(noticia);
        setImagemModal(noticia.imagem || null);

        const chave = cacheKey(noticia.link);

        if (conteudoCache.has(chave)) {
            const cached = conteudoCache.get(chave);
            setConteudoNoticia(cached.conteudo);
            if (cached.imagem) setImagemModal(cached.imagem);
            setCarregandoConteudo(false);
            return;
        }

        setConteudoNoticia(noticia.resumo || noticia.titulo);
        setCarregandoConteudo(true);
        carregarConteudoCompleto(noticia);
    };

    // ==============================================
    // 🔥 CARREGAR CONTEÚDO COMPLETO EM BACKGROUND (VIA BACKEND)
    // ==============================================
    const carregarConteudoCompleto = async (noticia) => {
        const userIdAtual = userIdRef.current;
        const chave = cacheKey(noticia.link);

        try {
            let conteudoFinal = noticia.resumo || noticia.titulo;
            let imagemFinal = noticia.imagem || null;

            if (!noticia.link || noticia.link === '#') {
                conteudoCache.set(chave, { conteudo: conteudoFinal, imagem: imagemFinal });
                if (isMountedRef.current) setCarregandoConteudo(false);
                return;
            }

            try {
                const response = await axios.get(`${BACKEND_URL}${encodeURIComponent(noticia.link)}`, {
                    timeout: 10000
                });

                if (response.data) {
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(response.data, 'text/html');

                    if (!imagemFinal) {
                        imagemFinal = doc.querySelector('meta[property="og:image"]')?.getAttribute('content')
                            || doc.querySelector('meta[name="twitter:image"]')?.getAttribute('content')
                            || doc.querySelector('link[rel="image_src"]')?.getAttribute('href')
                            || null;

                        if (!imagemFinal) {
                            const imgEl = doc.querySelector('article img, .article img, .post-content img, .entry-content img');
                            if (imgEl) {
                                imagemFinal = imgEl.getAttribute('src') || imgEl.getAttribute('data-src') || imgEl.getAttribute('data-lazy-src');
                            }
                        }

                        if (imagemFinal && !imagemFinal.startsWith('http')) {
                            try { imagemFinal = new URL(imagemFinal, noticia.link).href; } catch (e) { imagemFinal = null; }
                        }
                    }

                    const seletoresConteudo = [
                        'article', '.article', '.post-content', '.entry-content',
                        '.content', '.story-content', '.text-content',
                        '.news-content', '.body-text', '.main-content',
                        '.article-body', '.post-body', '.entry'
                    ];

                    let elementoConteudo = null;
                    for (const seletor of seletoresConteudo) {
                        const encontrado = doc.querySelector(seletor);
                        if (encontrado) {
                            elementoConteudo = encontrado;
                            break;
                        }
                    }

                    if (elementoConteudo) {
                        const remover = elementoConteudo.querySelectorAll(
                            'script, style, iframe, .ad, .ads, .advertisement, .share, .social, .comments, nav, footer, header'
                        );
                        remover.forEach(el => el.remove());

                        const paragrafos = elementoConteudo.querySelectorAll('p, h2, h3, h4');
                        let textoExtraido = '';
                        paragrafos.forEach(p => {
                            const text = p.textContent?.trim();
                            if (text && text.length > 20) {
                                textoExtraido += text + '\n\n';
                            }
                        });

                        if (textoExtraido.length > 100) {
                            conteudoFinal = textoExtraido;
                        }
                    } else {
                        const paragrafos = doc.querySelectorAll('p');
                        let textoExtraido = '';
                        paragrafos.forEach(p => {
                            const text = p.textContent?.trim();
                            if (text && text.length > 50 && !text.includes('©') && !text.includes('direitos')) {
                                textoExtraido += text + '\n\n';
                            }
                        });
                        if (textoExtraido.length > 200) {
                            conteudoFinal = textoExtraido;
                        }
                    }
                }
            } catch (e) { }

            conteudoFinal = (conteudoFinal || '')
                .replace(/\[.*?\]/g, '')
                .replace(/\(.*?\)/g, '')
                .replace(/\s+/g, ' ')
                .trim();

            if (!conteudoFinal || conteudoFinal.length < 20) {
                conteudoFinal = noticia.titulo + '\n\n' + (noticia.resumo || '');
            }

            conteudoCache.set(chave, {
                conteudo: conteudoFinal,
                imagem: imagemFinal
            });

            // 🔥 só atualiza UI se ainda for o mesmo utilizador e a mesma notícia
            if (
                isMountedRef.current &&
                userIdRef.current === userIdAtual &&
                noticiaModal?.link === noticia.link
            ) {
                setConteudoNoticia(conteudoFinal);
                if (imagemFinal) setImagemModal(imagemFinal);
                setCarregandoConteudo(false);
            }
        } catch (e) {
            if (isMountedRef.current) setCarregandoConteudo(false);
        }
    };

    // ==============================================
    // 🔥 FECHAR MODAL DA NOTÍCIA
    // ==============================================
    const fecharModalNoticia = () => {
        setNoticiaModal(null);
        setConteudoNoticia('');
        setImagemModal(null);
        setCarregandoConteudo(false);
    };

    // ==============================================
    // 🔥 ABRIR NOTÍCIA NO NAVEGADOR (EXTERNO)
    // ==============================================
    const abrirNoticiaExterna = (link) => {
        if (link && link !== '#') {
            window.open(link, '_blank', 'noopener,noreferrer');
        }
    };

    // ==============================================
    // FORMATAR DATA
    // ==============================================
    const formatarData = (data) => {
        if (!data || isNaN(data.getTime())) return 'Data desconhecida';

        const agora = new Date();
        const diff = (agora - data) / 1000;

        if (diff < 60) return `${Math.floor(diff)}s atrás`;
        if (diff < 3600) return `${Math.floor(diff / 60)}min atrás`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h atrás`;
        if (diff < 604800) return `${Math.floor(diff / 86400)}d atrás`;

        return data.toLocaleDateString('pt-PT', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    // ==============================================
    // 🔥 FALLBACK DE IMAGEM NOS CARDS (quando a imagem falha)
    // ==============================================
    const handleImageError = async (noticia) => {
        const estadoAtual = imagensFallback[noticia.id];

        // Já falhou ou está a carregar → não repete
        if (estadoAtual === 'failed' || estadoAtual === 'loading') return;

        // Marca como 'loading' antes de pedir
        setImagensFallback((prev) => ({ ...prev, [noticia.id]: 'loading' }));

        const fallback = await buscarImagemFallback(noticia.link);

        if (!isMountedRef.current) return;

        setImagensFallback((prev) => ({
            ...prev,
            [noticia.id]: fallback || 'failed'
        }));
    };

    // ==============================================
    // 🔥 RENDERIZAR MODAL DA NOTÍCIA (VIA REACT PORTAL)
    // ==============================================
    const renderModalNoticia = () => {
        if (!noticiaModal) return null;

        const imagemParaMostrar = imagemModal || noticiaModal.imagem;

        return ReactDOM.createPortal(
            <div className="noticia-modal-overlay" onClick={fecharModalNoticia}>
                <div className="noticia-modal" onClick={(e) => e.stopPropagation()}>
                    <button className="noticia-modal-close" onClick={fecharModalNoticia}>
                        <Icon name="close" size={24} />
                    </button>

                    {imagemParaMostrar && (
                        <div className="noticia-modal-image">
                            <img
                                src={imagemParaMostrar}
                                alt={noticiaModal.titulo}
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                        </div>
                    )}

                    <div className="noticia-modal-content">
                        <div className="noticia-modal-header">
                            <div className="noticia-modal-fonte">
                                <Icon name="public" size={16} />
                                {noticiaModal.fonte}
                            </div>
                            <span className="noticia-modal-data">
                                <Icon name="access_time" size={14} />
                                {formatarData(noticiaModal.data)}
                            </span>
                        </div>

                        <h2 className="noticia-modal-titulo">{noticiaModal.titulo}</h2>

                        <div className="noticia-modal-corpo">
                            {conteudoNoticia.split('\n\n').map((paragrafo, index) => (
                                <p key={index}>{paragrafo}</p>
                            ))}
                            {carregandoConteudo && (
                                <div className="noticia-modal-carregando">
                                    <span className="spinner-tiny"></span>
                                    A carregar conteúdo completo...
                                </div>
                            )}
                        </div>

                        <div className="noticia-modal-footer">
                            <button
                                className="btn-abrir-externo"
                                onClick={() => abrirNoticiaExterna(noticiaModal.link)}
                            >
                                <Icon name="open_in_new" size={18} />
                                Abrir no site original
                            </button>
                            <button
                                className="btn-fechar-modal"
                                onClick={fecharModalNoticia}
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                </div>
            </div>,
            document.body
        );
    };

    // ==============================================
    // RENDERIZAÇÃO DO ESTADO VAZIO (MÓDULO DESATIVADO)
    // ==============================================
    if (!moduloAtivo) {
        return (
            <div className="noticias-container">
                <div className="noticias-header">
                    <h2>
                        <Icon name="newspaper" size={28} />
                        Notícias
                    </h2>
                </div>
                <div className="noticias-empty">
                    <Icon name="info" size={48} />
                    <h3>Módulo de Notícias Desativado</h3>
                    <p>Ative o módulo de notícias nas Definições para visualizar as notícias.</p>
                </div>
            </div>
        );
    }

    // ==============================================
    // RENDERIZAÇÃO PRINCIPAL
    // ==============================================
    return (
        <>
            <div className="noticias-container">
                {/* HEADER */}
                <div className="noticias-header">
                    <h2>
                        <Icon name="newspaper" size={28} />
                        Notícias
                        {noticias.length > 0 && (
                            <span className="noticias-count">
                                {noticias.length} notícias
                            </span>
                        )}
                    </h2>
                    <div className="noticias-controls">
                        <div className="noticias-search">
                            <Icon name="search" size={18} />
                            <input
                                type="text"
                                placeholder="Pesquisar notícias..."
                                value={pesquisa}
                                onChange={(e) => setPesquisa(e.target.value)}
                                disabled={carregando}
                            />
                            {pesquisa && (
                                <button className="clear-btn" onClick={() => setPesquisa('')}>
                                    <Icon name="close" size={16} />
                                </button>
                            )}
                        </div>

                        <button
                            className={`btn-refresh ${atualizando ? 'loading' : ''}`}
                            onClick={() => carregarNoticias(true)}
                            disabled={carregando || atualizando}
                        >
                            <span className="spinner-small"></span>
                            <span className="btn-text">
                                <Icon name="refresh" size={16} />
                                Atualizar
                            </span>
                        </button>
                    </div>
                </div>

                {ultimaAtualizacao && noticias.length > 0 && (
                    <div className="noticias-ultima-atualizacao">
                        <Icon name="access_time" size={14} />
                        Última atualização: {formatarData(ultimaAtualizacao)}
                        <span className="dot"></span>
                    </div>
                )}

                {carregando ? (
                    <div className="noticias-loading">
                        {[1, 2, 3, 4, 5, 6].map(i => (
                            <div key={i} className="skeleton-card">
                                <div className="skeleton-image"></div>
                                <div className="skeleton-content">
                                    <div className="skeleton-line title"></div>
                                    <div className="skeleton-line subtitle"></div>
                                    <div className="skeleton-line"></div>
                                    <div className="skeleton-line short"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : erro && noticias.length === 0 ? (
                    <div className="noticias-empty">
                        <Icon name="error_outline" size={48} color="var(--nt-danger)" />
                        <h3>Erro ao carregar notícias</h3>
                        <p>{erro}</p>
                        <button
                            className="btn-tentar-novamente"
                            onClick={() => carregarNoticias()}
                        >
                            <Icon name="refresh" size={18} />
                            Tentar Novamente
                        </button>
                    </div>
                ) : noticiasFiltradas.length === 0 ? (
                    <div className="noticias-empty">
                        <Icon name={pesquisa ? "search_off" : "info"} size={48} />
                        <h3>{pesquisa ? 'Nenhum resultado encontrado' : 'Sem notícias disponíveis'}</h3>
                        <p>
                            {pesquisa
                                ? `Não foram encontradas notícias para "${pesquisa}"`
                                : 'Não há notícias disponíveis. Verifique as fontes configuradas.'}
                        </p>
                    </div>
                ) : (
                    <div className="noticias-grid">
                        {noticiasFiltradas.map(noticia => {
                            // 🔥 Estado da imagem:
                            // - 'loading' → mostra spinner
                            // - 'failed'  → mostra placeholder
                            // - URL       → mostra imagem fallback
                            // - undefined → usa a imagem original do feed
                            const estadoImagem = imagensFallback[noticia.id];
                            const imagemSrc =
                                estadoImagem && estadoImagem !== 'failed' && estadoImagem !== 'loading'
                                    ? estadoImagem
                                    : noticia.imagem;

                            return (
                                <div key={noticia.id} className="noticia-card">
                                    {imagemSrc ? (
                                        <img
                                            src={imagemSrc}
                                            alt={noticia.titulo}
                                            className="noticia-image"
                                            loading="lazy"
                                            onError={() => handleImageError(noticia)}
                                        />
                                    ) : estadoImagem === 'loading' ? (
                                        <div className="noticia-image image-placeholder">
                                            <span className="spinner-small"></span>
                                        </div>
                                    ) : (
                                        <div className="noticia-image image-placeholder">
                                            <Icon name="image" size={48} />
                                        </div>
                                    )}
                                    <div className="noticia-content">
                                        <div className="noticia-fonte">
                                            <Icon name="public" size={14} />
                                            {noticia.fonte}
                                        </div>
                                        <h3
                                            className="noticia-titulo"
                                            onClick={() => abrirNoticia(noticia)}
                                        >
                                            {noticia.titulo}
                                        </h3>
                                        <p className="noticia-resumo">{noticia.resumo}</p>
                                        <div className="noticia-footer">
                                            <span className="noticia-data">
                                                <Icon name="access_time" size={14} />
                                                {formatarData(noticia.data)}
                                            </span>
                                            <button
                                                className="btn-ler-mais"
                                                onClick={() => abrirNoticia(noticia)}
                                            >
                                                Ler mais
                                                <Icon name="arrow_forward" size={14} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {!carregando && noticias.length > 0 && (
                    <div className="noticias-footer">
                        <Icon name="info" size={14} />
                        <span className="footer-info">
                            As notícias são atualizadas automaticamente a cada 10 minutos
                        </span>
                        <span className="separator">•</span>
                        <span>{noticiasFiltradas.length} de {noticias.length} notícias exibidas</span>
                        {fontesComErro.length > 0 && (
                            <>
                                <span className="separator">•</span>
                                <span className="erro-parcial">
                                    ⚠️ {fontesComErro.length} fonte(s) com erro
                                </span>
                            </>
                        )}
                    </div>
                )}
            </div>

            {renderModalNoticia()}
        </>
    );
}